import { Injectable } from '@angular/core';
import { Auth, onAuthStateChanged } from '@angular/fire/auth';
import { io, Socket } from 'socket.io-client';
import { environment } from 'src/environments/environment';

/**
 * WebSocket Service
 *
 * Manages WebSocket connection for real-time events.
 * Automatically authenticates with Firebase token to join user-specific rooms.
 *
 * Events received after authentication:
 * - chemical-import:progress - Progress during chemical CSV import
 * - chemical-import:complete - Chemical import completed
 * - chemical-import:failed - Chemical import failed
 */
@Injectable({
  providedIn: 'root'
})
export class WebSocketService {
  private socket: Socket;
  private isAuthenticated = false;

  constructor(private auth: Auth) {
    this.socket = io(environment.webSocketUrl, {
      autoConnect: false, // Don't connect until we have a user
      transports: ['websocket'], // Skip HTTP polling — avoids "Session ID unknown" on multi-instance deployments
    });

    this.socket.on('connect', () => {
      console.log('[WebSocket] Connected');
      this.authenticateIfNeeded();
    });

    this.socket.on('auth.success', (response: any) => {
      console.log('[WebSocket] Authenticated successfully:', response);
      this.isAuthenticated = true;
    });

    this.socket.on('auth.error', (error: any) => {
      console.error('[WebSocket] Authentication failed:', error);
      this.isAuthenticated = false;
    });

    this.socket.on('disconnect', () => {
      console.log('[WebSocket] Disconnected');
      this.isAuthenticated = false;
    });

    // Listen for auth state changes
    onAuthStateChanged(this.auth, (user) => {
      if (user) {
        // User signed in - connect and authenticate
        if (!this.socket.connected) {
          this.socket.connect();
        } else {
          this.authenticateIfNeeded();
        }
      } else {
        // User signed out - disconnect
        this.socket.disconnect();
        this.isAuthenticated = false;
      }
    });
  }

  /**
   * Authenticate the socket connection with Firebase token
   */
  private async authenticateIfNeeded(): Promise<void> {
    if (this.isAuthenticated) {
      return;
    }

    const user = this.auth.currentUser;
    if (!user) {
      console.log('[WebSocket] No user to authenticate');
      return;
    }

    try {
      const token = await user.getIdToken();
      console.log('[WebSocket] Sending authentication...');
      this.socket.emit('authenticate', { token });
    } catch (error) {
      console.error('[WebSocket] Failed to get ID token:', error);
    }
  }

  /**
   * Register an event handler
   */
  registerEventHandler(event: string, handler: (...args: any[]) => void): void {
    this.socket.on(event, handler);
  }

  /**
   * Unregister an event handler
   */
  unregisterEventHandler(event: string, handler?: (...args: any[]) => void): void {
    if (handler) {
      this.socket.off(event, handler);
    } else {
      this.socket.off(event);
    }
  }

  /**
   * Emit an event to the server
   */
  emitEvent(event: string, data?: any): void {
    this.socket.emit(event, data);
  }

  /**
   * Check if socket is connected and authenticated
   */
  get connected(): boolean {
    return this.socket.connected && this.isAuthenticated;
  }
}
